import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useTemplates, useCreateTemplate, useDeleteTemplate } from '../hooks/useTravelBuddy';
import type { ChecklistKind } from '../types';
import * as T from '../ui/inlineTheme';

const delBtn: React.CSSProperties = {
  padding: 12,
  border: 'none',
  borderRadius: 12,
  background: 'transparent',
  color: '#9CA3AF',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  WebkitAppearance: 'none',
  appearance: 'none',
};

export function TemplatesPage() {
  const { data: templates = [], isLoading } = useTemplates();
  const createT = useCreateTemplate();
  const delT = useDeleteTemplate();
  const [name, setName] = useState('');
  const [lines, setLines] = useState('');
  const [kind, setKind] = useState<ChecklistKind>('packing');

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const items = lines.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!name.trim()) return;
    createT.mutateAsync({ name: name.trim(), items, kind }).then(() => {
      setName('');
      setLines('');
    });
  };

  const submitStyle: React.CSSProperties = {
    ...T.btnPrimary,
    width: '100%',
    opacity: createT.isPending || !name.trim() ? 0.45 : 1,
  };

  const listItem: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    borderRadius: 14,
    border: '1px solid #3f3f46',
    backgroundColor: 'rgba(39, 39, 42, 0.45)',
    padding: '16px 14px',
    boxSizing: 'border-box',
  };

  return (
    <div style={T.page}>
      <h1 style={T.h1}>Templates</h1>
      <p style={T.bodyMuted}>
        Reusable packing and to-do list templates. Adding one to a trip copies items — templates are not modified.
      </p>

      <form
        onSubmit={onCreate}
        style={{
          ...T.cardSolid,
          marginTop: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 style={T.h2}>New template</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <span style={{ ...T.labelSmall, margin: 0 }}>Type</span>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="radio"
              name="tpl-kind"
              checked={kind === 'packing'}
              onChange={() => setKind('packing')}
            />
            <span style={{ fontFamily: T.font, fontSize: 15, color: '#E5E7EB' }}>Packing</span>
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="radio"
              name="tpl-kind"
              checked={kind === 'todo'}
              onChange={() => setKind('todo')}
            />
            <span style={{ fontFamily: T.font, fontSize: 15, color: '#E5E7EB' }}>To-do</span>
          </label>
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
          style={T.input}
        />
        <textarea
          value={lines}
          onChange={(e) => setLines(e.target.value)}
          placeholder="One item per line"
          rows={5}
          style={T.textarea}
        />
        <button
          type="submit"
          className="sf-btn-primary"
          disabled={createT.isPending || !name.trim()}
          style={submitStyle}
        >
          {createT.isPending ? 'Saving…' : 'Save template'}
        </button>
      </form>

      <h2 style={{ ...T.labelSmall, marginTop: 32, marginBottom: 10 }}>Saved templates</h2>
      {isLoading && <p style={T.bodyMuted}>Loading…</p>}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {templates.map((t) => (
          <li key={t.id} style={listItem}>
            <div>
              <p style={{ margin: 0, fontFamily: T.font, fontSize: 17, fontWeight: 600, color: '#E5E7EB' }}>
                {t.name}
              </p>
              <p style={{ margin: '6px 0 0', fontFamily: T.font, fontSize: 14, color: '#9CA3AF' }}>
                {(t.kind ?? 'packing') === 'todo' ? 'To-do' : 'Packing'} · {t.items.length} items
              </p>
            </div>
            <button
              type="button"
              aria-label={`Delete ${t.name}`}
              onClick={() => delT.mutate(t.id)}
              style={delBtn}
            >
              <Trash2 size={22} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
